import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import logger from '../utils/logger';

const LoginPage = () => {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const { login, loginWithGoogle, user } = useContext(AuthContext);
    const navigate = useNavigate();
    const googleButtonRef = useRef(null);
    const isGoogleLogin = useRef(false); // Google login flag

    useEffect(() => {
        // Sadece sayfa yüklendiğinde user varsa yönlendir (Google login değilse)
        if (user && !isGoogleLogin.current) {
            navigate('/');
        }
    }, [user, navigate]);

    const handleGoogleResponse = useCallback(async (response) => {
        isGoogleLogin.current = true; // Google login başlıyor
        const loadingToast = toast.loading('Signing in with Google...');
        const result = await loginWithGoogle(response.credential);
        
        logger.log('Google Login Result:', result);
        logger.log('User profileCompleted:', result.user?.profileCompleted);
        
        if (result.success) {
            toast.success('Successfully logged in!', { id: loadingToast });
            
            // Profil tamamlanmamışsa onboarding'e yönlendir
            if (!result.user.profileCompleted) {
                logger.log('Redirecting to onboarding...');
                navigate('/onboarding');
            } else {
                logger.log('Redirecting to home...');
                navigate('/');
            }
        } else {
            toast.error(result.message || 'Google sign-in failed', { id: loadingToast });
        }
        isGoogleLogin.current = false; // İşlem bitti
    }, [loginWithGoogle, navigate]);

    useEffect(() => {
        // Google Sign-In butonu oluştur
        if (window.google && googleButtonRef.current) {
            window.google.accounts.id.initialize({
                client_id: '927028296542-otv90gcva097t81esl70p7chqhhhoj5q.apps.googleusercontent.com',
                callback: handleGoogleResponse,
            });

            window.google.accounts.id.renderButton(
                googleButtonRef.current,
                { 
                    theme: 'outline', 
                    size: 'large',
                    width: googleButtonRef.current.offsetWidth,
                    text: 'signin_with',
                    shape: 'rectangular',
                }
            );
        }
    }, [handleGoogleResponse]);

    const onChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

    const onSubmit = async e => {
        e.preventDefault();
        if (loading) return; // Zaten loading ise çık
        
        setLoading(true);
        const loadingToast = toast.loading('Logging in...');
        const result = await login(formData.email, formData.password);
        
        if (result.success) {
            toast.success('Successfully logged in!', { id: loadingToast });
            navigate('/');
        } else {
            toast.error(result.message, { id: loadingToast });
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                className="max-w-md w-full card p-8">
            <h2 className="text-3xl font-bold text-center mb-6">Log In</h2>
            <form onSubmit={onSubmit} className="space-y-4">
                <input type="email" name="email" value={formData.email} onChange={onChange} required placeholder="Email Address" className="input-field"/>
                <input type="password" name="password" value={formData.password} onChange={onChange} required placeholder="Password" className="input-field"/>
                <div className="text-right">
                    <Link to="/forgot-password" className="text-sm text-light-primary dark:text-dark-primary hover:underline">
                        Forgot Password?
                    </Link>
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full !text-lg disabled:opacity-50 disabled:cursor-not-allowed">
                    {loading ? 'Logging in...' : 'Log In'}
                </button>
            </form>
            
            {/* Divider */}
            <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-light-border dark:border-dark-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-light-card dark:bg-dark-card text-light-text/60 dark:text-dark-text/60">
                        Or continue with
                    </span>
                </div>
            </div>

            {/* Google Sign-In Button */}
            <div ref={googleButtonRef} className="w-full"></div>

            <p className="text-center mt-6">
                Don't have an account?{' '}
                <Link to="/register" className="font-semibold text-light-primary dark:text-dark-primary hover:underline">
                    Sign Up
                </Link>
            </p>
            </motion.div>
        </div>
    );
};

export default LoginPage;